import { Button, Tabs } from 'antd';
import type { TabsProps } from 'antd';
import React, { useEffect, useState } from 'react';
import s from './s.module.less';
import HeaderImage from './images/img1.png';
import Benefits from './components/Benefits';
import Features from './components/Features';
import FAQs from './components/FAQs';
import { setHasViewKiwiEhrPage } from 'utils/localstore';
import FullScreenPopLayout from 'components/FullScreenPopLayout';
import FrequencyQuestionSecion from 'components/FrequencyQuestionSecion';
import { FAQ } from './constant';
import { Email } from 'constants/contact';

type Props = {
    notifyHasConfig?: () => void;
    hideLearnMoreButton?: boolean; // 是否隐藏 Learn more 按钮（当从 Learn more 弹窗进入时隐藏）
    showTips: boolean; // 是否显示提示（由外部传入）
    onOpenReviewGuide: () => void; // How it works 里 "Show me how to send an invitation" 的入口
};

const IntroHomeView = (props: Props) => {
    const { notifyHasConfig, hideLearnMoreButton = false, showTips, onOpenReviewGuide } = props;
    const [activeId, setActiveId] = useState('solutions');
    const [isScrolling, setIsScrolling] = useState(false);

    const [showGuide, setShowGuide] = useState(false);

    const guideItems: TabsProps['items'] = [
        {
            key: 'benefits',
            label: <div>Benefits</div>,
            children: <div />,
        },
        {
            key: 'features',
            label: <div>How does it work</div>,
            children: <div />,
        },
        {
            key: 'faqs',
            label: <div>FAQs</div>,
            children: <div />,
        },
    ];

    const handleChange = (activeKey: string) => {
        setIsScrolling(true);
        setActiveId(activeKey);

        setTimeout(() => {
            const element = document.getElementById(activeKey);
            if (element) {
                element.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }
            // 滚动完成后重置标志
            setTimeout(() => {
                setIsScrolling(false);
            }, 500);
        }, 50);
    };

    useEffect(() => {
        const els = document.querySelectorAll('.ant-tabs-tab');
        const activeClassName = 'ant-tabs-tab-active';
        if (isScrolling) {
            return;
        }
        els.forEach((el) => {
            const classList = Array.from(el.classList).filter((i) => i !== activeClassName);
            if (el.getAttribute('data-node-key') === `${activeId}`) {
                classList.push(activeClassName);
                el.className = classList.join(' ');
            } else {
                el.className = 'ant-tabs-tab';
            }
        });
    }, [activeId, isScrolling]);

    useEffect(() => {
        setHasViewKiwiEhrPage('true');
        const observer = new IntersectionObserver(
            (entries) => {
                if (isScrolling) {
                    return;
                }
                // Find the section that is most visible in the viewport
                const visibleSections = entries
                    .filter((entry) => entry.isIntersecting)
                    .map((entry) => ({
                        id: entry.target.id,
                        ratio: entry.intersectionRatio,
                        top: entry.boundingClientRect.top,
                    }))
                    .sort((a, b) => {
                        // Prefer sections with higher intersection ratio
                        if (Math.abs(a.ratio - b.ratio) > 0.1) {
                            return b.ratio - a.ratio;
                        }
                        // If ratios are similar, prefer the one closer to top
                        return Math.abs(a.top) - Math.abs(b.top);
                    });

                if (visibleSections.length > 0) {
                    setActiveId(visibleSections[0].id);
                }
            },
            {
                threshold: [0, 0.1, 0.3, 0.5, 0.7, 1],
            },
        );
        const watchingItems = Array.from(document.getElementsByClassName('watchingContent'));
        watchingItems.forEach((item) => observer.observe(item));
        return () => {
            observer.disconnect();
        };
    }, [isScrolling]);

    return (
        <div className={s.wrapper}>
            <div className={s.wrapperIn}>
                <div className={s.header}>
                    <div className={s.headerLeft}>
                        <h1 className={s.headerTitle}>
                            Unlock the power of patient feedback with
                            <br />
                            <span>Klarity reviews</span>
                        </h1>
                        <p className={s.headerInfo}>
                            Turn patient experiences into your most powerful marketing tool. Our automated system makes
                            it easy to collect and leverage reviews to help you build trust, attract new patients, and
                            grow your practice.
                        </p>
                        {!showTips && !hideLearnMoreButton && (
                            <div className={s.headerBtnWrap}>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        // 直接进入 dashboard，不再显示 guide 弹窗
                                        if (notifyHasConfig) {
                                            notifyHasConfig();
                                        }
                                    }}
                                    size="large"
                                    className={s.startedButton}
                                >
                                    Learn more
                                </Button>
                            </div>
                        )}
                        {showTips && (
                            <div className={s.tips}>
                                <div className={s.tipIcon} />
                                <div className={s.tipContent}>
                                    <div className={s.tipDesc}>
                                        Before using this feature, you&apos;ll need to complete UniProfile and be listed
                                        on Klarity Health.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className={s.headerRight}>
                        <img src={HeaderImage} className={s.headerImage} alt="Klarity reviews" />
                    </div>
                </div>
                <div className={s.contentWrap}>
                    <div className={s.tabs} id="ehrHomeTab">
                        <Tabs
                            defaultActiveKey="1"
                            activeKey={activeId}
                            items={guideItems}
                            onChange={(activeKey) => handleChange(activeKey)}
                        />
                    </div>
                    <div className={`${s.content} watchingContent`} id="benefits">
                        <Benefits />
                    </div>
                    <div className={`${s.content} watchingContent`} id="features">
                        <Features onOpenReviewGuide={onOpenReviewGuide} />
                    </div>
                    <div className={`${s.faqsSection} watchingContent`} id="faqs">
                        <FAQs />
                    </div>
                </div>
                {showGuide && (
                    <FullScreenPopLayout
                        footerButton={
                            <div className={s.btnWrap}>
                                <div className={s.helpWrap}>
                                    Need help? Contact{' '}
                                    <a href={`mailto:${Email.provider_support}`}>providersupport@helloklarity.com</a>{' '}
                                    for more information.
                                </div>
                                <Button
                                    onClick={() => {
                                        // 先关闭弹窗
                                        setShowGuide(false);
                                        // 然后调用回调函数，切换显示 PPAKlarityReviewsDashboradPage
                                        if (notifyHasConfig) {
                                            notifyHasConfig();
                                        }
                                    }}
                                    type="primary"
                                    className={s.manageButton}
                                >
                                    Manage your reputation
                                </Button>
                            </div>
                        }
                    >
                        <div className={s.popWrap}>
                            <div className={s.title}>Your ultimate guide for the Klarity Reviews</div>
                            <div className={s.des}>
                                Klarity reviews is designed to empower you to build trust, attract new patients, and
                                grow your practice. We&apos;ve made it effortlessly to collect valuable patient
                                feedback, showcase your success, and foster stronger patient relationships.
                            </div>
                            <div className={s.faqWrap}>
                                <div className={s.line} />
                                <FrequencyQuestionSecion data={FAQ} largerFontsize />
                            </div>
                        </div>
                    </FullScreenPopLayout>
                )}
            </div>
        </div>
    );
};

export default IntroHomeView;
